import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  Activity,
  AlertTriangle,
  ChevronDown,
  Command,
  KeyRound,
  LogOut,
  Menu,
  Moon,
  Search,
  SlidersHorizontal,
  Sun,
  User as UserIcon,
} from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { useTheme } from '@/auth/ThemeProvider';
import { Button } from '@/components/ui/Button';
import { Kbd } from '@/components/ui/Kbd';
import { Sheet } from '@/components/ui/Sheet';
import { Tooltip } from '@/components/ui/Tooltip';
import { Badge } from '@/components/ui/Badge';
import { ChangePasswordDialog } from '@/components/layout/ChangePasswordDialog';
import { StorePicker } from '@/components/filters/StorePicker';
import { DateRangePicker } from '@/components/filters/DateRangePicker';
import { TimeRangeModeToggle } from '@/components/filters/TimeRangeModeToggle';
import { CompareToggle } from '@/components/filters/CompareToggle';
import { SavedViewsButton } from '@/components/filters/SavedViewsButton';
import { useCommandPalette } from '@/components/ui/CommandPalette';
import { filtersForPath, type FilterKey } from '@/features/registry';
import { useFilters, rangeISO } from '@/state/filters';
import { useOverview } from '@/api/bi';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';
import { Logo } from './Logo';
import { useMobileNav } from './MobileNavContext';

/**
 * Topbar — primary filter rail + identity zone.
 *
 *   left:  date-range, store picker, period-compare segmented
 *   right: live ticker · ⌘K trigger · theme · account menu (password · sign out)
 *
 * The live ticker is a quiet but valuable nudge: an always-on "what's
 * happening right now" chip that polls /api/bi/overview every minute and
 * lights up if exceptions appear. Hovering reveals the breakdown.
 */
export function Topbar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const palette = useCommandPalette();
  const mobileNav = useMobileNav();
  const { pathname } = useLocation();
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const filters = useMemo(() => filtersForPath(pathname), [pathname]);
  const hasFilters = filters.length > 0;

  return (
    <header
      className={cn(
        'sticky top-0 z-30 flex items-center gap-x-3 gap-y-2 border-b border-border bg-background/80 px-3 py-2 backdrop-blur supports-[backdrop-filter]:bg-background/70 sm:px-4',
        'min-h-14',
      )}
    >
      <button
        type="button"
        aria-label="Open navigation"
        onClick={mobileNav.toggle}
        className="press inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border bg-surface text-muted-foreground hover:text-foreground focus-ring lg:hidden"
      >
        <Menu className="h-4 w-4" aria-hidden />
      </button>

      <Logo size={22} withWordmark={false} className="lg:hidden" />

      {/*
       * The rail is only rendered for routes that read these filters — see
       * ROUTE_FILTERS. It also stays off small screens entirely: five controls
       * wrapping onto a second row ate a third of a phone viewport, so the
       * button beside it opens them in a drawer instead.
       */}
      {hasFilters ? (
        <div className="hidden min-w-0 flex-1 items-center gap-2 md:flex md:flex-wrap">
          <FilterRail filters={filters} />
        </div>
      ) : null}

      <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
        {hasFilters ? (
          <Button
            variant="outline"
            size="sm"
            className="md:hidden"
            onClick={() => setFilterDrawerOpen(true)}
          >
            <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
            Filters
          </Button>
        ) : null}

        <LiveTicker />

        {/*
         * Two renderings of the same action. The keyboard hint is meaningless
         * without a keyboard, and the old trigger was `hidden sm:inline-flex`,
         * so on a phone the command palette simply could not be opened.
         */}
        <Tooltip content="Command palette · ⌘K">
          <button
            type="button"
            onClick={palette.toggle}
            className="press hidden h-8 items-center gap-2 rounded-md border border-border bg-surface px-2 text-xs text-muted-foreground hover:text-foreground focus-ring sm:inline-flex"
          >
            <Command className="h-3.5 w-3.5" aria-hidden />
            <span className="hidden md:inline">Quick jump</span>
            <Kbd>⌘K</Kbd>
          </button>
        </Tooltip>
        <Button
          variant="ghost"
          size="icon"
          onClick={palette.toggle}
          aria-label="Search and jump to a page"
          className="sm:hidden"
        >
          <Search className="h-4 w-4" />
        </Button>

        <Tooltip content={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}>
          <Button variant="ghost" size="icon" onClick={toggle} aria-label="Toggle theme">
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
        </Tooltip>

        <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />

        <DropdownMenu.Root open={accountMenuOpen} onOpenChange={setAccountMenuOpen}>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              className={cn(
                'press inline-flex h-8 max-w-[11rem] items-center gap-1.5 rounded-md border border-border bg-surface px-2 text-left text-xs font-medium text-foreground focus-ring sm:max-w-[14rem]',
              )}
              aria-label="Account menu"
            >
              <UserIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
              <span className="min-w-0 flex-1 truncate">{user?.name ?? user?.username ?? 'You'}</span>
              {user?.role ? (
                <Badge tone="primary" className="hidden shrink-0 capitalize sm:inline-flex">
                  {user.role}
                </Badge>
              ) : null}
              <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={6}
              collisionPadding={12}
              className={cn(
                'z-[1000] min-w-[11rem] rounded-md border border-border bg-surface-elevated p-1 shadow-soft outline-none',
                'data-[state=open]:animate-in data-[state=closed]:animate-out',
                'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                'duration-150 ease-[cubic-bezier(0.22,1,0.36,1)]',
              )}
            >
              <DropdownMenu.Item
                className="press flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-primary/10 data-[highlighted]:text-primary"
                onSelect={(e) => {
                  e.preventDefault();
                  setAccountMenuOpen(false);
                  setChangePasswordOpen(true);
                }}
              >
                <KeyRound className="h-3.5 w-3.5" aria-hidden />
                Change password…
              </DropdownMenu.Item>
              <DropdownMenu.Separator className="my-1 h-px bg-border" />
              <DropdownMenu.Item
                className="press flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-destructive outline-none data-[highlighted]:bg-destructive/10 data-[highlighted]:text-destructive"
                onSelect={(e) => {
                  e.preventDefault();
                  setAccountMenuOpen(false);
                  void logout();
                }}
              >
                <LogOut className="h-3.5 w-3.5" aria-hidden />
                Sign out
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>

      <Sheet
        open={filterDrawerOpen}
        onOpenChange={setFilterDrawerOpen}
        side="right"
        title="Filters"
        description="Applies to every figure on this page."
      >
        <div className="flex flex-col items-start gap-3">
          <FilterRail filters={filters} />
        </div>
      </Sheet>
    </header>
  );
}

/**
 * The filter controls this route declares, in a fixed order so the rail does
 * not reshuffle as you move between pages.
 */
function FilterRail({ filters }: { filters: FilterKey[] }) {
  const show = (key: FilterKey) => filters.includes(key);
  return (
    <>
      {show('date') ? <DateRangePicker /> : null}
      {show('timeMode') ? <TimeRangeModeToggle /> : null}
      {show('store') ? <StorePicker /> : null}
      {show('compare') ? (
        <>
          <span className="hidden h-6 w-px bg-border md:inline-block" aria-hidden />
          <CompareToggle />
        </>
      ) : null}
      <SavedViewsButton />
    </>
  );
}

/**
 * Always-on "what's happening" chip. Reuses the existing /api/bi/overview/
 * hook (already polled every 60s on Overview/Live Ops) so we ride the same
 * cache — zero extra requests when those pages are open.
 */
function LiveTicker() {
  const range = useFilters((s) => s.range);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);
  const storeId = useFilters((s) => s.storeId);
  const iso = useMemo(() => rangeISO(range, timeRangeMode), [range, timeRangeMode]);
  const { data, isLoading } = useOverview({ start: iso.start, end: iso.end, storeId });

  if (isLoading || !data) {
    return (
      <span className="hidden h-8 items-center gap-2 rounded-md border border-border bg-surface px-2.5 text-xs text-muted-foreground md:inline-flex">
        <Activity className="h-3.5 w-3.5 opacity-60" aria-hidden />
        <span className="font-mono opacity-60">--</span>
      </span>
    );
  }

  const inFlight = data.in_flight.total;
  const exceptions = data.exceptions;
  const hasExceptions = exceptions > 0;

  return (
    <Tooltip
      content={
        <div className="space-y-0.5 px-1 py-0.5">
          <div className="font-medium">Live snapshot</div>
          <div className="text-muted-foreground">
            {fmt.number(inFlight)} in flight · {fmt.number(exceptions)} exception
            {exceptions === 1 ? '' : 's'}
          </div>
        </div>
      }
    >
      <button
        type="button"
        className={cn(
          'press hidden h-8 items-center gap-2 rounded-md border border-border bg-surface px-2.5 text-xs focus-ring md:inline-flex',
          hasExceptions && 'border-warning/50 text-warning-soft',
        )}
      >
        <span className={cn('live-dot', hasExceptions ? 'live-dot-warning' : 'live-dot-success')} aria-hidden />
        <span className="font-mono tabular-nums">{fmt.number(inFlight)}</span>
        <span className="text-muted-foreground">in flight</span>
        {hasExceptions ? (
          <span className="inline-flex items-center gap-1 border-l border-border pl-2 text-warning">
            <AlertTriangle className="h-3 w-3" aria-hidden />
            {fmt.number(exceptions)}
          </span>
        ) : null}
      </button>
    </Tooltip>
  );
}
